import { UseFetchInfiniteHookProps } from "../models/UseFetchInfiniteHookProps";
import {
	fetchEquipmentList,
	PageOfEquipmentWithFilterParam,
} from "../controllers/CensusCoolerController";
import useSWRInfinite from "swr/infinite";
import * as React from "react";
import { concatenateApiSJSONListResponse } from "../utils";
import { JSONList } from "../models/JSONList";
import { VeboxModels } from "../../../../../types/veboxModels";

export type UseFetchEquipmentWithFilterListInfiniteHookProps =
	UseFetchInfiniteHookProps<
		JSONList<VeboxModels.JSONEquipment>,
		VeboxModels.JSONEquipment
	>;

export const useEquipmentWithFilterListHook = (
	params: PageOfEquipmentWithFilterParam,
): UseFetchEquipmentWithFilterListInfiniteHookProps => {
	const swrKey = (index: number) => ({
		...params,
		offset: index * (params?.limit ?? 40),
	});

	const { data, error, setSize, isLoading, isValidating, mutate } =
		useSWRInfinite<JSONList<VeboxModels.JSONEquipment>, Error>(
			swrKey,
			params?.shouldFetch ? fetchEquipmentList : null,
		);

	const isFetching = isLoading;

	const concatenatedData = React.useMemo(
		() => (params?.shouldFetch ? concatenateApiSJSONListResponse(data) : []),
		[data, params?.shouldFetch],
	);

	const isNextPageLoading = isValidating;
	let isRevalidatingFirstPage = false;

	const handleOnLoadNextPage = async (startIndex?: number) => {
		if (startIndex === undefined) {
			await setSize(0);
			isRevalidatingFirstPage = true;
		}
		await setSize(startIndex + 1);
	};

	return {
		mutate,
		lastPageInfo:
			data?.[data.length - 1] ?? ({} as JSONList<VeboxModels.JSONEquipment>),
		flattenedData: concatenatedData.length ? concatenatedData : [],
		error,
		isNextPageLoading,
		isRevalidatingFirstPage,
		isFetching,
		handleOnLoadNextPage,
		totalCount: data?.[0]?.total ?? 0,
		size: params?.limit,
	};
};
